import React, { useState, useEffect } from 'react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import Starfield from '../components/Starfield'
import GlassCard from '../components/GlassCard'
import StatCard from '../components/StatCard'
import WorkingAPI from '../services/WorkingAPI'
import { 
  Crown, 
  Shield, 
  Star, 
  TrendingUp, 
  TrendingDown,
  Search,
  ShoppingCart,
  Coins,
  Plus,
  Diamond,
  Target,
  Gem,
  ArrowUpDown
} from 'lucide-react'

interface MarketItem {
  id: number
  name: string
  price: number
  quality: string
  category: string
  description: string
  trend: string
  quantity: number
  seller: string
  image: string
}

const Market = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [sortBy, setSortBy] = useState('price')
  const [cart, setCart] = useState<MarketItem[]>([])
  const [marketApiData, setMarketApiData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const workingAPI = WorkingAPI.getInstance()

  const playerCredits = 45670
  const marketStats = {
    totalListings: 1247,
    activeTraders: 89,
    todayVolume: 2340000,
    yourListings: 12
  }

  // Load market data from working API
  useEffect(() => {
    const loadMarketData = async () => {
      try {
        setIsLoading(true)
        const result = await workingAPI.getMarketData()
        
        if (result.status === 200 && result.data) {
          setMarketApiData(result.data)
          console.log('Market API Data:', result.data)
        } else {
          console.log('Market API response:', result)
          setMarketApiData({ 
            status: result.error || 'Connected to market API, implementation needed',
            timestamp: new Date().toISOString()
          })
        }
      } catch (error) {
        console.error('Failed to load market data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadMarketData()
  }, [])

  const categories = [
    { id: 'all', name: 'All Items', icon: Crown },
    { id: 'resources', name: 'Resources', icon: Diamond },
    { id: 'equipment', name: 'Equipment', icon: Shield },
    { id: 'ships', name: 'Ships', icon: Target },
    { id: 'technology', name: 'Technology', icon: Star },
    { id: 'artifacts', name: 'Artifacts', icon: Gem }
  ]

  const marketItems = [
    {
      id: 1,
      name: "Quantum Plasma Cells",
      category: "resources",
      seller: "Galactic Mining Corp",
      price: 12500,
      quantity: 100,
      quality: "Legendary",
      trend: "up",
      description: "High-energy plasma cells for advanced propulsion systems",
      image: "/api/placeholder/64/64"
    },
    {
      id: 2,
      name: "Void Shield Generator",
      category: "equipment",
      seller: "Stellar Defense Systems",
      price: 89000,
      quantity: 5,
      quality: "Epic",
      trend: "stable",
      description: "Advanced shield technology providing maximum protection",
      image: "/api/placeholder/64/64"
    },
    {
      id: 3,
      name: "Nebula-class Cruiser",
      category: "ships",
      seller: "Imperial Shipyards",
      price: 2500000,
      quantity: 1,
      quality: "Legendary",
      trend: "down",
      description: "Heavy cruiser with enhanced firepower and armor",
      image: "/api/placeholder/64/64"
    },
    {
      id: 4,
      name: "Dark Matter Crystals",
      category: "resources",
      seller: "Void Harvesters",
      price: 8900,
      quantity: 250,
      quality: "Rare",
      trend: "up",
      description: "Rare crystals essential for exotic technology research",
      image: "/api/placeholder/64/64"
    },
    {
      id: 5,
      name: "AI Combat Protocol",
      category: "technology",
      seller: "Cybernetic Innovations",
      price: 156000,
      quantity: 3,
      quality: "Epic",
      trend: "up",
      description: "Advanced AI system for autonomous combat operations",
      image: "/api/placeholder/64/64"
    },
    {
      id: 6,
      name: "Ancient Relic Core",
      category: "artifacts",
      seller: "Archaeological Guild",
      price: 750000,
      quantity: 1,
      quality: "Legendary",
      trend: "stable",
      description: "Mysterious artifact with unknown technological properties",
      image: "/api/placeholder/64/64"
    }
  ]

  const getQualityColor = (quality: string) => {
    switch (quality.toLowerCase()) {
      case 'legendary': return 'cosmic'
      case 'epic': return 'nebula'
      case 'rare': return 'stellar'
      case 'uncommon': return 'success'
      case 'common': return 'void'
      default: return 'glow'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-400" />
      case 'down': return <TrendingDown className="h-4 w-4 text-red-400" />
      default: return <ArrowUpDown className="h-4 w-4 text-gray-400" />
    }
  }

  const filteredItems = marketItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.seller.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const addToCart = (item: MarketItem) => {
    setCart(prev => [...prev, item])
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Starfield />
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <GlassCard variant="premium" className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
                  <Crown className="mr-3 h-8 w-8 text-yellow-400" />
                  Galactic Trade Exchange
                </h1>
                <p className="text-purple-300">The galaxy's premier marketplace for resources, equipment, and technology</p>
              </div>
              <div className="text-right">
                <div className="flex items-center space-x-2 mb-2">
                  <Coins className="h-5 w-5 text-yellow-400" />
                  <span className="text-xl font-bold text-white">{playerCredits.toLocaleString()}</span>
                  <span className="text-yellow-300">Credits</span>
                </div>
                <Button variant="default">
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Cart ({cart.length})
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Market Statistics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Market Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Listings"
              value={marketStats.totalListings.toLocaleString()}
              icon={<Crown className="h-5 w-5" />}
              variant="premium"
            />
            <StatCard
              title="Active Traders"
              value={marketStats.activeTraders.toString()}
              icon={<Shield className="h-5 w-5" />}
              variant="epic"
            />
            <StatCard
              title="24h Volume"
              value={`${(marketStats.todayVolume / 1000000).toFixed(1)}M`}
              icon={<TrendingUp className="h-5 w-5" />}
              variant="legendary"
            />
            <StatCard
              title="Your Listings"
              value={marketStats.yourListings.toString()}
              icon={<Star className="h-5 w-5" />}
              variant="default"
            />
          </div>
        </div>

        {/* Categories and Search */}
        <div className="mb-8">
          <GlassCard variant="epic" className="p-6">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="flex items-center"
                  >
                    <category.icon className="mr-2 h-4 w-4" />
                    {category.name}
                  </Button>
                ))}
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-300" />
                  <Input
                    variant="stellar"
                    placeholder="Search items or sellers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
                <Button variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Sell Item
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Market Listings */}
        <div className="mb-8">
          <GlassCard variant="default" className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Market Listings</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-300">Sort by:</span>
                <Button
                  variant={sortBy === 'price' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('price')}
                >
                  Price
                </Button>
                <Button
                  variant={sortBy === 'name' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('name')}
                >
                  Name
                </Button>
                <Button
                  variant={sortBy === 'quality' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('quality')}
                >
                  Quality
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredItems.map((item) => (
                <GlassCard key={item.id} variant="premium" className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                        {item.category === 'resources' && <Diamond className="h-6 w-6 text-white" />}
                        {item.category === 'equipment' && <Shield className="h-6 w-6 text-white" />}
                        {item.category === 'ships' && <ShoppingCart className="h-6 w-6 text-white" />}
                        {item.category === 'technology' && <Star className="h-6 w-6 text-white" />}
                        {item.category === 'artifacts' && <Crown className="h-6 w-6 text-white" />}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">{item.name}</h3>
                        <p className="text-sm text-purple-300">{item.seller}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={getQualityColor(item.quality)}>
                        {item.quality}
                      </Badge>
                      {getTrendIcon(item.trend)}
                    </div>
                  </div>

                  <p className="text-gray-300 text-sm mb-4">{item.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center space-x-2">
                        <Coins className="h-4 w-4 text-yellow-400" />
                        <span className="text-xl font-bold text-white">{item.price.toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-400">{item.quantity} available</p>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="default"
                      className="flex-1"
                      onClick={() => addToCart(item)}
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Add to Cart
                    </Button>
                    <Button variant="outline" size="sm">
                      View Details
                    </Button>
                  </div>
                </GlassCard>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <GlassCard variant="premium" className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Resource Exchange</h3>
            <p className="text-green-300 text-sm mb-4">Convert excess resources into credits</p>
            <Button variant="default" className="w-full">
              <Diamond className="mr-2 h-4 w-4" />
              Sell Resources
            </Button>
          </GlassCard>

          <GlassCard variant="epic" className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Auction House</h3>
            <p className="text-yellow-300 text-sm mb-4">Bid on rare and exclusive items</p>
            <Button variant="secondary" className="w-full">
              <Crown className="mr-2 h-4 w-4" />
              View Auctions
            </Button>
          </GlassCard>

          <GlassCard variant="legendary" className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Trade Contracts</h3>
            <p className="text-pink-300 text-sm mb-4">Establish long-term trading partnerships</p>
            <Button variant="ghost" className="w-full">
              <Star className="mr-2 h-4 w-4" />
              Create Contract
            </Button>
          </GlassCard>
        </div>
      </div>
    </div>
  )
}

export default Market